import { Currency } from '../bank-account/bank-account';
import { S3Object } from '../cash-funding/cash-funding';
import { CodedDocumentKey } from '../trip/trip';
import { EmployeeGlAccountKey } from '../employee-account/employee-account';

/** Mirrors Finance.Models ExpenseReceiptStatus. */
export enum ExpenseReceiptStatus {
  Incomplete = 1,
  InPool = 2,
  InClaim = 3,
  Posted = 4,
  Rejected = 5,
}

/** Finance.Models ApRateSource.Manual — the receipt carries a typed rate. */
export const MANUAL_RATE_SOURCE = 5;

/** Global.Models GlobalEntityDefinition codes a receipt owner can carry. */
export enum ReceiptOwnerCode {
  Trip = 66,
  PettyCashFund = 70,
}

/** Global.Models EntityKey. */
export interface EntityKey {
  reference: string;
  number: string;
  entityDefinitionCode: number;
}

/** A line's share charged to one cost center, with its own expense account. */
export interface ExpenseReceiptSplit {
  id: string;
  costCenter: CodedDocumentKey | null;
  /** This cost center's share, ISV included. */
  total: number;
  /** Derived by the API: before ISV. */
  amount: number;
  /** Derived by the API: total − amount. */
  taxAmount: number;
  resolvedGlAccount: EmployeeGlAccountKey | null;
  resolvedByRuleReference: string | null;
  /** The expense account Accounting set in its review; wins over the rule's. */
  glAccountOverride: EmployeeGlAccountKey | null;
}

export interface ExpenseReceiptLine {
  id: string;
  description: string | null;
  concept: CodedDocumentKey | null;
  /** 0.15, or null when the line carries no ISV. */
  taxRate: number | null;
  /** The line's cost centers; a line not split has one. */
  splits: ExpenseReceiptSplit[];
  /** Σ splits, ISV included. */
  total: number;
  amount: number;
  taxAmount: number;
}

/** A petty cash or trip receipt (#5791) — mirrors Finance.Models ExpenseReceiptDto. */
export interface ExpenseReceipt {
  id: string;
  /** e.g. NT-RCPT-2026-000001 */
  number: string;
  financialCompanyReference: string;
  owner: EntityKey;
  vendor: CodedDocumentKey | null;
  vendorTaxId: string | null;
  fiscalNumber: string | null;
  /** CAI in Honduras. */
  taxAuthorizationNumber: string | null;
  date: string | null;
  currency: Currency;
  exchangeRate: number;
  rateToUsd: number | null;
  rateSource: number;
  lines: ExpenseReceiptLine[];
  attachments: S3Object[];
  subtotal: number;
  taxTotal: number;
  total: number;
  totalUsd: number | null;
  /** The company's functional currency and the total in it (total × exchangeRate); null until rated. */
  functionalCurrency: Currency;
  functionalTotal: number | null;
  isTaxRecoverable: boolean;
  missingFields: string[];
  capturedBy: string;
  /** The expense claim holding it while In claim or Posted. */
  claimReference: string | null;
  claimNumber: string | null;
  /** Why an approver rejected it out of a claim. */
  rejectionComment: string | null;
  status: ExpenseReceiptStatus;
}

export interface ExpenseReceiptSplitUpdate {
  id: string | null;
  costCenterReference: string | null;
  /** ISV included; the API derives the amount before ISV and the ISV. */
  total: number;
  /** Accounting review only; ignored on capture. */
  glAccountOverrideReference?: string | null;
}

export interface ExpenseReceiptLineUpdate {
  id: string | null;
  description: string | null;
  conceptCode: string | null;
  taxRate: number | null;
  splits: ExpenseReceiptSplitUpdate[];
}

export interface ExpenseReceiptUpdate {
  id: string | null;
  vendorReference: string | null;
  fiscalNumber: string | null;
  /** CAI in Honduras. */
  taxAuthorizationNumber: string | null;
  /** yyyy-MM-dd */
  date: string | null;
  currency: Currency;
  rateToUsd: number | null;
  lines: ExpenseReceiptLineUpdate[];
}

export interface OwnerReference {
  reference: string;
  entityDefinitionCode: number;
}

export interface ExpenseReceiptBatchCommand {
  owner: OwnerReference;
  receipts: ExpenseReceiptUpdate[];
}

export interface ExpenseReceiptBatchItem {
  index: number;
  receipt: ExpenseReceipt | null;
  errorEn: string | null;
  errorEs: string | null;
}

export interface ExpenseReceiptOwner {
  owner: EntityKey;
  name: string;
  /** The fund's currency; null for a trip (HNL or USD). */
  currency: Currency | null;
}

export interface ExpenseReceiptVendor {
  reference: string;
  code: string | null;
  name: string;
  taxId: string | null;
}

export interface ExpenseReceiptPool {
  receipts: ExpenseReceipt[];
  inPool: number;
  incomplete: number;
  /** Rejected out of a claim, waiting to be corrected or deleted. */
  rejected: number;
  taxTotal: number;
  total: number;
}
